import { Button } from "react-bootstrap"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useGetPropertyAmenitiesQuery, useAddNewPropertyAmenityMutation, useDeletePropertyAmenityMutation } from "../../../services/bClientApi"
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import canAccess from "../../../services/accessService/accessService"
import SelectAmenityModal from "../../Modals/SelectAmenityModal/SelectAmenityModal"
import { AMENITY_TAG_OPTIONS, AmenityType } from "../../../Types/amenityTypes"
import useAmenityRefs from "../../../Hooks/UseAmenities/useAmenityRefsHook"
import { BoxArrowUpRight } from "react-bootstrap-icons"
import { SelectPropertyModal } from "../../Modals/SelectPropertyModal/selectPropertyModal"
import CopyPropertyAmenitiesModal from "../../Modals/CopyPropertyAmenitiesModal/CopyPropertyAmenitiesModal"
import { PropertyType } from "../../../Types/propertyTypes"

export default function PropertyAmenities(props: {
    propertyId?: string
}) {
    const { propertyId } = props
    const dispatch = useAppDispatch()

    const [selectModalActive, setSelectModalActive] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [selectPropertyModalActive, setSelectPropertyModalActive] = useState(false)
    const [copySourceProperty, setCopySourceProperty] = useState<PropertyType | null>(null)

    const { data: propertyAmenitiesData } = useGetPropertyAmenitiesQuery({
        propertyAmenitiesQuery: { propertyId },
        pagination: { page: 1, limit: 500, all: true },
    }, { skip: !propertyId })

    const amenityRef = useAmenityRefs()

    const [addNewPropertyAmenity, { isLoading: isAdding }] = useAddNewPropertyAmenityMutation()
    const [deletePropertyAmenity, { isLoading: isDeleting }] = useDeletePropertyAmenityMutation()

    const propertyAmenityList = propertyAmenitiesData?.docs || []

    const assignedAmenityIds = new Set(propertyAmenityList.map((pa: any) => pa.amenityId))

    const sortedPropertyAmenities = [...propertyAmenityList].sort((a: any, b: any) => {
        const nameA = amenityRef[a.amenityId]?.title || ''
        const nameB = amenityRef[b.amenityId]?.title || ''
        return nameA.localeCompare(nameB)
    })

    const tagLabelMap: Record<string, string> = {}
    AMENITY_TAG_OPTIONS.forEach((tagOption) => { tagLabelMap[tagOption.tag] = tagOption.text })

    // amenities with multiple tags appear in each matching group; untagged amenities fall under "Other"
    const groupedByTag = sortedPropertyAmenities.reduce((groups: Record<string, any[]>, pa: any) => {
        const amenity = amenityRef[pa.amenityId]
        const tags = amenity?.tags && amenity.tags.length > 0 ? amenity.tags : ['other']
        tags.forEach((tag: string) => {
            if (!groups[tag]) groups[tag] = []
            groups[tag].push(pa)
        })
        return groups
    }, {})

    const orderedTagKeys = [
        ...AMENITY_TAG_OPTIONS.map((tagOption) => tagOption.tag).filter((tag) => groupedByTag[tag]),
        ...Object.keys(groupedByTag).filter((tag) => tag !== 'other' && !tagLabelMap[tag]).sort(),
        ...(groupedByTag['other'] ? ['other'] : []),
    ]


    const handleAddAmenities = async (amenityData: AmenityType | AmenityType[]) => {
        const amenities = Array.isArray(amenityData) ? amenityData : [amenityData]

        let successCount = 0
        let failCount = 0

        for (const amenity of amenities) {
            try {
                await addNewPropertyAmenity({
                    propertyId,
                    amenityId: amenity._id,
                    overrides: {},
                }).unwrap()
                successCount++
            } catch (err) {
                console.error('Failed to add amenity:', err)
                failCount++
            }
        }

        if (successCount > 0) {
            dispatch(showSuccess(`${successCount} amenit${successCount === 1 ? 'y' : 'ies'} added`))
        }
        if (failCount > 0) {
            dispatch(showError(`Failed to add ${failCount} amenit${failCount === 1 ? 'y' : 'ies'}`))
        }
        setSelectModalActive(false)
    }

    const handleRemoveAmenity = async (propertyAmenityId: string) => {
        try {
            await deletePropertyAmenity(propertyAmenityId).unwrap()
            dispatch(showSuccess('Amenity removed'))
        } catch (err) {
            console.error('Failed to remove amenity:', err)
            dispatch(showError('Failed to remove amenity'))
        }
    }

    const copyFromPropertyButton = canAccess('routes', 'edit-property-amenity') && (
        <Button
            size="sm"
            variant="outline-primary"
            onClick={() => setSelectPropertyModalActive(true)}
        >
            Copy From Property
        </Button>
    )

    return (
        <>
            {propertyAmenityList.length > 0 && (
                <div className="property-amenities-list">
                    <h4>Property Amenities</h4>

                    <div className="d-flex gap-2 my-2">
                        <Button
                            size="sm"
                            variant={editMode ? 'danger' : 'primary'}
                            onClick={() => setEditMode(!editMode)}
                        >
                            {editMode ? 'Stop Editing' : 'Edit Amenities'}
                        </Button>
                        {copyFromPropertyButton}
                    </div>

                    {orderedTagKeys.map((tagKey) => (
                        <div key={tagKey} className="mb-3">
                            <h6 className="text-uppercase text-muted mt-2">
                                {tagKey === 'other' ? 'Other' : (tagLabelMap[tagKey] || tagKey)}
                            </h6>
                            {groupedByTag[tagKey].map((pa: any) => {
                                const amenity = amenityRef[pa.amenityId]
                                return (
                                    <div key={pa._id} className="mb-2 p-2 border border-1 rounded d-flex gap-3 align-items-center">
                                        {editMode && canAccess('routes', 'edit-property-amenity') && (
                                            <Button
                                                size="sm"
                                                disabled={isDeleting}
                                                variant="danger"
                                                onClick={() => handleRemoveAmenity(pa._id)}
                                            >
                                                X
                                            </Button>
                                        )}
                                        <div className="d-flex gap-2 align-items-center flex-wrap">
                                            <div className="fw-bold">
                                                {amenity?.title || pa.amenityId}
                                            </div>
                                            {amenity?.type && (
                                                <span className="small text-muted">{amenity.type}</span>
                                            )}
                                            {canAccess('routes', 'amenity') && amenity?._id && (
                                                <Link to={`/amenity/${amenity._id}`} className="small">
                                                    <BoxArrowUpRight size={14} viewBox={'0 0 20 20'} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ))}

                    {editMode && canAccess('routes', 'edit-property-amenity') && (
                        <Button
                            className="mb-2"
                            size="sm"
                            disabled={isAdding}
                            variant="success"
                            onClick={() => setSelectModalActive(true)}
                        >
                            + Add Amenity
                        </Button>
                    )}
                </div>
            )}

            {propertyAmenityList.length === 0 && canAccess('routes', 'edit-property-amenity') && (
                <div className="property-amenities-list">
                    <h4>Property Amenities</h4>
                    <p className="text-muted">No amenities assigned to this property.</p>
                    <Button
                        className="mb-2"
                        size="sm"
                        disabled={isAdding}
                        variant="success"
                        onClick={() => setSelectModalActive(true)}
                    >
                        + Add Amenity
                    </Button>
                    <span className="ms-2">{copyFromPropertyButton}</span>
                </div>
            )}

            {selectPropertyModalActive && (
                <SelectPropertyModal
                    active={selectPropertyModalActive}
                    onClose={() => setSelectPropertyModalActive(false)}
                    onSelect={(propertyData) => {
                        const selected = Array.isArray(propertyData) ? propertyData[0] : propertyData
                        if (selected) setCopySourceProperty(selected)
                    }}
                    title="Copy Amenities From"
                    message="Select the property to copy amenities from"
                    filterFn={(property: PropertyType) => property._id !== propertyId}
                />
            )}

            {copySourceProperty && propertyId && (
                <CopyPropertyAmenitiesModal
                    active={true}
                    onClose={() => setCopySourceProperty(null)}
                    targetPropertyId={propertyId}
                    sourceProperty={copySourceProperty}
                />
            )}

            {selectModalActive && (
                <SelectAmenityModal
                    active={selectModalActive}
                    onClose={() => setSelectModalActive(false)}
                    onSelect={handleAddAmenities}
                    title="Select Amenities"
                    message="Select amenities to add to this property"
                    multiSelect={true}
                    filterFn={(amenity: AmenityType) => !assignedAmenityIds.has(amenity._id || '')}
                />
            )}
        </>
    )
}
